import React, { useState, useEffect, useRef } from 'react';
import { 
  BookOpen, User as UserIcon, X, Heart, Mail, Lock, Globe, ArrowRight, 
  ShieldCheck, ShieldAlert, Settings, Volume2, VolumeX, Smartphone, 
  Moon, Sun, Coffee, LogOut, Trash2, Bell, ExternalLink, Info, Star,
  RefreshCw, Play, Swords, Scroll, Trophy, Type, Share2, Check, Sparkles, WifiOff
} from 'lucide-react';
import { COBALT_BLUE, APP_VERSION, SUPPORT_LINK } from '../constants';
import { User, AppSettings } from '../types';
import { makePlayPurchase, restorePurchases, isNativePlayBillingSupported } from '../utils/playBilling';

// ... (IntroVideo and Login components)

export const ParentalGate: React.FC<{ 
  isOpen: boolean; 
  onClose: () => void; 
  onSuccess: () => void;
  title?: string;
  message?: string;
}> = ({ isOpen, onClose, onSuccess, title, message }) => {
  const [val, setVal] = useState('');
  const [num1] = useState(Math.floor(Math.random() * 8) + 7);
  const [num2] = useState(Math.floor(Math.random() * 8) + 7);
  const answer = num1 + num2;

  if (!isOpen) return null;

  const handleVerify = () => {
    if (parseInt(val) === answer) {
      onSuccess();
      onClose();
    } else {
      alert('Incorrect answer. Please ask a parent or guardian for help.');
      setVal('');
    }
  };

  return (
    <div className="fixed inset-0 z-[500] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-blue-950/80 backdrop-blur-xl" onClick={onClose}></div>
      <div className="relative bg-white w-full max-w-sm rounded-[3rem] p-10 shadow-2xl animate-in zoom-in duration-300 text-center">
        <div className="bg-amber-50 p-6 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
          <Lock className="w-10 h-10 text-amber-500" />
        </div>
        <h2 className="font-cinzel font-bold text-xl mb-2 text-gray-900">{title || 'Parental Gate'}</h2>
        <p className="text-gray-500 text-sm mb-8 leading-relaxed font-medium">
          {message || 'To ensure intentional action by a supervising adult, please solve this sum:'}
        </p>
        
        <div className="bg-gray-50 p-8 rounded-[2rem] mb-8 border-2 border-dashed border-gray-100">
          <p className="text-3xl font-bold text-blue-600 tracking-widest">{num1} + {num2} = ?</p>
        </div>

        <input 
          type="number" 
          pattern="\d*"
          inputMode="numeric"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="Sum"
          className="w-full p-4 bg-gray-100 rounded-2xl mb-6 text-center text-2xl font-bold outline-none focus:ring-4 focus:ring-blue-100 transition-all font-mono"
          autoFocus
          onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
        />

        <div className="flex flex-col gap-3">
          <button 
            onClick={handleVerify}
            className="w-full bg-blue-600 text-white py-5 rounded-2xl font-bold shadow-lg hover:bg-blue-700 transition active:scale-95"
          >
            Verify & Proceed
          </button>
          <button 
            onClick={onClose}
            className="w-full py-2 text-[10px] text-gray-400 font-bold uppercase tracking-widest"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

interface ArchiveScoreRecord {
  puzzleTitle: string;
  formattedDate: string;
  code: string;
  timestamp: number;
}

const ArchiveItemRow: React.FC<{
  record: ArchiveScoreRecord;
}> = ({ record }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copiedSocial, setCopiedSocial] = useState(false);
  const [copiedExcel, setCopiedExcel] = useState(false);
  const [copiedCSV, setCopiedCSV] = useState(false);
  const [showTournamentQuery, setShowTournamentQuery] = useState(false);
  const [gateAction, setGateAction] = useState<(() => void) | null>(null);

  const handleGateProtectedAction = (action: () => void) => {
    setGateAction(() => action);
  };

  // Helper parser for recorded code (excel TSV format)
  const parseRecord = () => {
    try {
      const lines = record.code.split('\n');
      if (lines.length >= 2) {
        const parts = lines[1].split('\t');
        if (parts.length >= 6) {
          return {
            playerId: parts[0] || "Guest_Player",
            formattedDate: parts[1] || record.formattedDate,
            puzzleTitle: parts[2] || record.puzzleTitle,
            moves: parseInt(parts[3]) || 0,
            duration: parseInt(parts[4]) || 0,
            faithScore: parseFloat(parts[5]) || 0
          };
        }
      }
    } catch (e) {
      console.error("Failed to parse archived score code", e);
    }
    return {
      playerId: "Guest_Player",
      formattedDate: record.formattedDate,
      puzzleTitle: record.puzzleTitle,
      moves: 0,
      duration: 0,
      faithScore: 0
    };
  };

  const parsed = parseRecord();
  const parsedFaithScore = Math.round(parsed.faithScore * 10) / 10;

  // 1. Social Sharing Text
  const socialShareText = `✝️ Bible Puzzle Quiz App ✝️
🏆 Faith score recorded: ${parsedFaithScore} points!

🧩 Puzzle: "${parsed.puzzleTitle}"
👤 Player ID: ${parsed.playerId}
⏰ Completed: ${parsed.formattedDate}
📊 Moves: ${parsed.moves}
⏱️ Duration: ${parsed.duration} seconds
✨ Faith Score: ${parsedFaithScore}

(Lower moves and duration and higher Faith Scores are better)`;

  // 2. Direct submit logic
  const scoreText = [
    parsed.playerId,
    `"${parsed.formattedDate}"`,
    `"${parsed.puzzleTitle}"`,
    parsed.moves,
    parsed.duration,
    parsedFaithScore
  ].join(",");

  const bpQuestBaseUrl = "https://www.bpquest.name.ng"; 
  const bpQuestUrl = `${bpQuestBaseUrl}/?score=${encodeURIComponent(scoreText)}`;

  const copyToClipboard = (text: string, type: 'social' | 'excel' | 'csv') => {
    const handleSuccess = () => {
      if (type === 'social') {
        setCopiedSocial(true);
        setTimeout(() => setCopiedSocial(false), 2000);
      } else if (type === 'excel') {
        setCopiedExcel(true);
        setTimeout(() => setCopiedExcel(false), 2000);
      } else if (type === 'csv') {
        setCopiedCSV(true);
        setTimeout(() => setCopiedCSV(false), 2000);
      }
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text)
        .then(handleSuccess)
        .catch(() => fallbackCopy(text));
    } else {
      fallbackCopy(text);
    }

    function fallbackCopy(textString: string) {
      const textArea = document.createElement("textarea");
      textArea.value = textString;
      textArea.style.position = "fixed";
      textArea.style.top = "0";
      textArea.style.left = "0";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        const success = document.execCommand('copy');
        if (success) {
          handleSuccess();
        } else {
          alert('Could not copy automatically. Please copy the text manually.');
        }
      } catch (err) {
        alert('Could not copy automatically. Please copy the text manually.');
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <>
      <ParentalGate 
        isOpen={!!gateAction} 
        onClose={() => setGateAction(null)} 
        onSuccess={() => {
          if (gateAction) gateAction();
          setGateAction(null);
        }}
        title="Adult Verification"
        message="To continue, please have an adult solve this sum:"
      />
      <div className="flex items-center justify-between gap-3 p-3 bg-white border border-slate-100 rounded-2xl hover:border-slate-250 transition-all shadow-sm">
        <div className="flex flex-col min-w-0">
          <span className="font-extrabold text-slate-800 text-xs truncate max-w-[180px] uppercase tracking-wide leading-tight">
            {record.puzzleTitle}
          </span>
          <span className="text-[10px] text-slate-400 font-semibold leading-normal mt-0.5">
            {record.formattedDate}
          </span>
        </div>
        <button
          onClick={() => setIsOpen(true)}
          className="flex items-center justify-center gap-1 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold text-[10px] rounded-xl transition-all shrink-0 uppercase tracking-wider cursor-pointer"
        >
          <Share2 className="w-3.5 h-3.5 animate-pulse" />
          <span>Actions</span>
        </button>
      </div>

      {isOpen && (
        <div className="fixed inset-0 z-[450] flex items-center justify-center p-4 pt-safe pb-safe px-safe">
          {/* Backdrop screen */}
          <div 
            className="absolute inset-0 bg-slate-950/65 backdrop-blur-xs transition-opacity"
            onClick={() => {
              setIsOpen(false);
              setShowTournamentQuery(false);
            }}
          />

          {/* Modal Container */}
          <div className="relative bg-white w-full max-w-md rounded-[2.5rem] p-6 md:p-8 shadow-2xl border border-slate-100 text-left overflow-y-auto max-h-[85vh] animate-in fade-in zoom-in-95 duration-200">
            <div className="flex justify-between items-start mb-5 pb-4 border-b border-slate-100">
              <div>
                <h3 className="font-cinzel font-bold text-lg text-slate-900 flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-amber-500" />
                  <span>Score Details</span>
                </h3>
                <p className="text-[11px] text-slate-400 font-semibold mt-1 uppercase tracking-wider">
                  {parsed.puzzleTitle}
                </p>
              </div>
              <button 
                onClick={() => {
                  setIsOpen(false);
                  setShowTournamentQuery(false);
                }}
                className="p-1 px-2 text-sm font-bold text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-xl transition-all active:scale-95 cursor-pointer"
              >
                ✕
              </button>
            </div>

            {/* Quick Stats Grid */}
            <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4 mb-5 grid grid-cols-2 gap-3 text-xs leading-normal">
              <div className="col-span-2 flex items-center justify-between border-b border-slate-100 pb-2 mb-1">
                <span className="text-slate-500 font-medium font-sans">Player ID:</span>
                <span className="font-mono font-semibold text-slate-800 break-all text-right select-all pl-2">{parsed.playerId}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-slate-500 font-medium">Moves:</span>
                <span className="text-slate-900 font-bold text-sm mt-0.5">{parsed.moves}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-slate-500 font-medium">Duration:</span>
                <span className="text-slate-900 font-bold text-sm mt-0.5">{parsed.duration}s</span>
              </div>
              <div className="col-span-2 pt-2 border-t border-slate-100 flex items-center justify-between">
                <span className="text-slate-700 font-bold flex items-center gap-1">
                  <span>✨</span> Faith Score:
                </span>
                <span className="text-[#0047ab] font-extrabold text-base">{parsedFaithScore}</span>
              </div>
            </div>

            {/* Action Options */}
            <div className="space-y-4">
              {/* (2) Direct submission to BPQuest with tournament quiz */}
              {showTournamentQuery ? (
                <div className="bg-orange-50 border border-orange-100 rounded-2xl p-4 space-y-3 animate-in fade-in zoom-in-95 duration-150">
                  <div className="flex items-center gap-1.5 text-orange-900 font-bold text-xs">
                    <span>⚠️</span>
                    <span>Tournament Check</span>
                  </div>
                  <p className="text-[11px] text-orange-850 leading-relaxed font-semibold">
                    Are you currently participating in a tournament at the BPQuest website?
                  </p>
                  <div className="grid grid-cols-2 gap-2 pt-1 font-bold text-xs">
                    <button
                      onClick={() => {
                        setShowTournamentQuery(false);
                        handleGateProtectedAction(() => {
                          window.location.assign(bpQuestUrl);
                        });
                      }}
                      className="py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl transition active:scale-95 cursor-pointer text-center"
                    >
                      Yes, submit
                    </button>
                    <button
                      onClick={() => {
                        setShowTournamentQuery(false);
                      }}
                      className="py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl transition active:scale-95 cursor-pointer text-center"
                    >
                      No, close
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowTournamentQuery(true)}
                  className="w-full flex items-center justify-between p-3 px-4 bg-orange-50 hover:bg-orange-100/70 border border-orange-100 rounded-2xl transition active:scale-[0.99] group text-left cursor-pointer"
                >
                  <div className="pr-3">
                    <span className="text-xs font-bold text-orange-800 uppercase tracking-wider block flex items-center gap-1">
                      <span>⚡</span> Direct Submit to BPQuest
                    </span>
                    <span className="text-[10px] text-orange-700 mt-0.5 block leading-relaxed">
                      Send score directly to tournament server at bpquest.name.ng.
                    </span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-orange-600 group-hover:translate-x-0.5 transition-transform shrink-0" />
                </button>
              )}

              {/* (1) Social Sharing Options */}
              <div className="border border-slate-100 bg-slate-50/50 rounded-2xl p-4">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2.5 block">
                  (1) Social Sharing
                </span>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => handleGateProtectedAction(() => {
                      window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(socialShareText)}`, '_blank');
                    })}
                    className="flex flex-col items-center justify-center p-2.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 rounded-xl transition active:scale-95 text-center cursor-pointer text-xs font-bold font-sans"
                  >
                    <span className="text-lg">💬</span>
                    <span className="text-[10px] font-bold mt-1">WhatsApp</span>
                  </button>

                  <button
                    onClick={() => handleGateProtectedAction(() => {
                      window.open(`https://t.me/share/url?url=${encodeURIComponent(socialShareText)}`, '_blank');
                    })}
                    className="flex flex-col items-center justify-center p-2.5 bg-sky-50 hover:bg-sky-100 text-sky-800 rounded-xl transition active:scale-95 text-center cursor-pointer text-xs font-bold font-sans"
                  >
                    <span className="text-lg">✈️</span>
                    <span className="text-[10px] font-bold mt-1">Telegram</span>
                  </button>

                  <button
                    onClick={() => copyToClipboard(socialShareText, 'social')}
                    className="flex flex-col items-center justify-center p-2.5 bg-blue-50 hover:bg-blue-100 text-blue-800 rounded-xl transition active:scale-95 cursor-pointer text-center"
                  >
                    {copiedSocial ? (
                      <>
                        <Check className="w-5 h-5 text-emerald-600" />
                        <span className="text-[10px] font-bold text-emerald-700 mt-1">Copied!</span>
                      </>
                    ) : (
                      <>
                        <Share2 className="w-4 h-4 text-blue-600" />
                        <span className="text-[10px] font-bold mt-1.5">Copy Text</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* (3) BPQuest values sharing to paste */}
              <div className="border border-slate-100 bg-slate-50/50 rounded-2xl p-4 space-y-2.5">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 block">
                  (3) BPQuest Values Sharing to Paste
                </span>
                <div className="grid grid-cols-2 gap-2 text-xs font-semibold">
                  <button
                    onClick={() => copyToClipboard(record.code, 'excel')}
                    className="flex items-center justify-center gap-1.5 py-2.5 px-3 bg-slate-800 hover:bg-slate-900 text-white rounded-xl transition active:scale-95 cursor-pointer"
                  >
                    {copiedExcel ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-emerald-300" />
                        <span className="text-emerald-300">Copied Excel!</span>
                      </>
                    ) : (
                      <>
                        <span>📋</span>
                        <span>Copy Excel TSV</span>
                      </>
                    )}
                  </button>

                  <button
                    onClick={() => copyToClipboard(scoreText, 'csv')}
                    className="flex items-center justify-center gap-1.5 py-2.5 px-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition active:scale-95 cursor-pointer"
                  >
                    {copiedCSV ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-emerald-200" />
                        <span className="text-emerald-200">Copied BPQuest!</span>
                      </>
                    ) : (
                      <>
                        <span>🔑</span>
                        <span>Copy BPQuest CSV</span>
                      </>
                    )}
                  </button>
                </div>
                <p className="text-[9px] text-slate-400 leading-normal">
                  Copy formatted lines suitable for direct paste into spreadsheets or scoring tools.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export const SettingsModal: React.FC<{ 
  isOpen: boolean; 
  onClose: () => void; 
  user: User | null;
  settings: AppSettings;
  onUpdateSettings: (s: Partial<AppSettings>) => void;
  onLogout: () => void;
  onDeleteAccount: () => void;
  onResetProgress: () => void;
  onOpenProfile: () => void;
  onOpenReleaseNotes: () => void;
  onOpenSupport?: () => void;
  triggerOnlineAction?: (featureName: string, action: () => void) => void;
}> = ({ isOpen, onClose, user, settings, onUpdateSettings, onLogout, onDeleteAccount, onResetProgress, onOpenProfile, onOpenReleaseNotes, onOpenSupport, triggerOnlineAction }) => {
  const [gateAction, setGateAction] = useState<(() => void) | null>(null);
  const [showSponsorshipExplanation, setShowSponsorshipExplanation] = useState(false);
  const [isArchiveOpen, setIsArchiveOpen] = useState(false);
  const [archive, setArchive] = useState<ArchiveScoreRecord[]>([]);

  useEffect(() => {
    if (isOpen) {
      try {
        const stored = localStorage.getItem('bp_quiz_score_archive');
        if (stored) {
          setArchive(JSON.parse(stored));
        } else {
          setArchive([]);
        }
      } catch (e) {
        console.error("Failed to load score archive", e);
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleGateProtectedAction = (action: () => void) => {
    setGateAction(() => action);
  };

  const handleUrlAction = (name: string, url: string, target = '_blank') => {
    const action = () => window.open(url, target);
    if (triggerOnlineAction) {
      triggerOnlineAction(name, () => handleGateProtectedAction(action));
    } else {
      handleGateProtectedAction(action);
    }
  };

  const handleShareApp = async () => {
    const shareData = {
      title: 'Bible Puzzle Quiz',
      text: 'Shalom! I have found a really great app! Depict the Word through play with Bible Puzzle Quiz. See depictions and read and listen to your favorite Bible stories. Solve scripture puzzles, challenge friends to Faith Duels, and grow in the Word! Try it today!',
      url: SUPPORT_LINK
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      // Fallback: Copy to clipboard or open mail
      const shareText = `${shareData.text}\n\nDownload here: ${shareData.url}`;
      const fallbackCopy = (text: string) => {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.top = "0";
        textArea.style.left = "0";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          const successful = document.execCommand('copy');
          if (successful) {
            alert('Share link copied to clipboard! You can now paste it to invite your friends.');
          } else {
            alert('Could not auto-copy the text. Please copy this manually:\n\n' + text);
          }
        } catch (err) {
          alert('Could not auto-copy the text. Please copy this manually:\n\n' + text);
        }
        document.body.removeChild(textArea);
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(shareText)
          .then(() => {
            alert('Share link copied to clipboard! You can now paste it to invite your friends.');
          })
          .catch(() => {
            fallbackCopy(shareText);
          });
      } else {
        fallbackCopy(shareText);
      }
    }
  };

  const sections = [
    {
      title: 'Account & Faith Score',
      icon: <Trophy className="w-5 h-5 text-emerald-600" />,
      items: [
        {
          label: 'My Profile',
          description: 'View stats and change avatar',
          control: (
            <button 
              onClick={onOpenProfile}
              className="flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-xl text-blue-600 font-bold text-xs hover:bg-blue-100 transition-colors"
            >
              View Profile
              <ArrowRight className="w-3 h-3" />
            </button>
          )
        },
        {
          label: 'Score Archive',
          description: archive.length > 0 ? `Device cache of past ${archive.length} tournament score codes` : 'No score codes archived on this device yet',
          control: (
            <button 
              onClick={() => setIsArchiveOpen(!isArchiveOpen)}
              className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-xl text-slate-700 font-bold text-xs hover:bg-slate-100 transition-colors"
            >
              {isArchiveOpen ? 'Hide' : `View (${archive.length})`}
            </button>
          ),
          extra: isArchiveOpen ? (
            <div className="mt-4 p-4 bg-slate-50/70 border border-slate-100 rounded-2xl space-y-3 px-3 py-3 w-full text-left col-span-2 block animate-in fade-in slide-in-from-top-1 duration-200 transition-all">
              {archive.length > 0 ? (
                <div className="space-y-3">
                  <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Last 3 tournament score codes (excel/BPQuest format):</p>
                  {archive.map((record, idx) => (
                    <ArchiveItemRow key={idx} record={record} />
                  ))}
                </div>
              ) : (
                <div className="text-center text-xs text-gray-500 font-medium py-3">
                  Your past three Faith Scores will be archived here.
                </div>
              )}
            </div>
          ) : null
        },
        {
          label: 'Sign Out',
          description: 'End current session',
          control: (
            <button onClick={onLogout} className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors">
              <LogOut className="w-5 h-5" />
            </button>
          )
        },
        {
          label: 'Reset Progress',
          description: 'Clear stats, keep account',
          control: (
            <button onClick={onResetProgress} className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors">
              <RefreshCw className="w-5 h-5" />
            </button>
          )
        },
        {
          label: 'Delete Account',
          description: 'Permanent removal',
          control: (
            <button onClick={onDeleteAccount} className="p-2 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors">
              <Trash2 className="w-5 h-5" />
            </button>
          )
        }
      ]
    },
    {
      title: 'Display & Story',
      icon: <Type className="w-5 h-5 text-blue-600" />,
      items: [
        {
          label: 'Story Text Size',
          description: 'Adjust reading comfort',
          control: (
            <div className="flex items-center gap-1 bg-gray-50 p-1 rounded-xl border border-gray-100">
              {[14, 16, 18, 20, 24].map((size) => (
                <button
                  key={size}
                  onClick={() => onUpdateSettings({ textSize: size })}
                  className={`w-8 h-8 rounded-lg font-bold text-xs transition-all ${
                    settings.textSize === size 
                    ? 'bg-blue-600 text-white shadow-md' 
                    : 'text-gray-400 hover:bg-white'
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          )
        }
      ]
    },
    {
      title: 'Legal & Support',
      icon: <ShieldCheck className="w-5 h-5 text-amber-600" />,
      items: [
        {
          label: 'How to Play',
          description: 'Game instructions',
          control: (
            <button 
              onClick={() => handleUrlAction('Game Instructions', "https://sites.google.com/view/bpqpi/play-instructions")} 
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <Info className="w-5 h-5" />
            </button>
          )
        },
        {
          label: 'Privacy Policy',
          description: 'How we handle data',
          control: (
            <button 
              onClick={() => handleUrlAction('Privacy Policy', "https://sites.google.com/view/bpqprivacypolicy/home")} 
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <ExternalLink className="w-5 h-5" />
            </button>
          )
        },
        {
          label: 'Terms of Service',
          description: 'App usage rules',
          control: (
            <button 
              onClick={() => handleUrlAction('Terms of Service', "https://sites.google.com/view/bpqtos/terms-of-service")} 
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <ShieldAlert className="w-5 h-5" />
            </button>
          )
        },
        {
          label: 'Contact Us',
          description: 'theswgames@gmail.com',
          control: (
            <button 
              onClick={() => handleUrlAction('Email Contact', "mailto:theswgames@gmail.com", '_self')} 
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <Mail className="w-5 h-5" />
            </button>
          )
        },
        {
          label: 'Tell a Friend',
          description: 'Share the app with others',
          control: (
            <button 
              onClick={() => {
                const shareAction = () => handleGateProtectedAction(handleShareApp);
                if (triggerOnlineAction) {
                  triggerOnlineAction('App Sharing', shareAction);
                } else {
                  shareAction();
                }
              }}
              className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
            >
              <Share2 className="w-5 h-5" />
            </button>
          )
        },
        {
          label: 'Version',
          description: APP_VERSION,
          control: (
            <button 
              onClick={onOpenReleaseNotes}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <Info className="w-5 h-5" />
            </button>
          )
        }
      ]
    },
    {
      title: 'Monetization',
      icon: <Heart className="w-5 h-5 text-rose-600" />,
      items: [
        {
          label: 'Sponsorship Options',
          description: 'Unlock and support options',
          control: (
            <button 
              onClick={() => setShowSponsorshipExplanation(true)}
              className="p-2 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
              title="Unlock & Support"
            >
              <Heart className="w-5 h-5 fill-rose-500 animate-pulse" />
            </button>
          )
        },
        {
          label: 'Restore Status',
          description: 'Sync sponsorship',
          control: (
            <button 
              onClick={() => handleGateProtectedAction(() => alert('Syncing sponsorship status...'))}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          )
        },
        {
          label: 'Rate the App',
          description: 'Support the mission',
          control: (
            <button 
              onClick={() => handleUrlAction('App Rating & Support', SUPPORT_LINK)}
              className="p-2 text-amber-500 hover:bg-amber-50 rounded-lg transition-colors"
            >
              <Star className="w-5 h-5" />
            </button>
          )
        }
      ]
    }
  ];

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 pt-safe pb-safe px-safe">
      <ParentalGate 
        isOpen={!!gateAction} 
        onClose={() => setGateAction(null)} 
        onSuccess={() => {
          if (gateAction) gateAction();
          setGateAction(null);
        }}
      />

      {showSponsorshipExplanation && (
        <div className="fixed inset-0 z-[400] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-950/75 backdrop-blur-md" onClick={() => setShowSponsorshipExplanation(false)}></div>
          <div className="relative bg-white w-full max-w-md p-8 rounded-[2.5rem] shadow-2xl border border-slate-100 flex flex-col items-center text-center animate-in zoom-in-95 duration-200">
            <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center mb-6">
              <Heart className="w-8 h-8 text-rose-500 fill-rose-500 animate-pulse" />
            </div>
            
            <h3 className="font-cinzel font-bold text-2xl text-slate-900 mb-4">Sponsorship Options</h3>
            
            <div className="space-y-4 text-slate-600 text-sm leading-relaxed mb-8">
              <p>
                We have two unique ways you can support our Bible-centered mission:
              </p>
              <div className="bg-slate-50 p-4 rounded-2xl text-left space-y-3 border border-slate-100">
                <div className="flex gap-2.5">
                  <span className="text-base">🔑</span>
                  <div>
                    <h4 className="font-bold text-slate-800 text-xs uppercase tracking-wide">1. One-Time Full Library Unlock</h4>
                    <p className="text-xs text-slate-500 mt-0.5">Unlock all current and future 100+ puzzles and Faith Duels permanently with a single contribution of **$1 to $5**.</p>
                  </div>
                </div>
                <div className="h-px bg-slate-100"></div>
                <div className="flex gap-2.5">
                  <span className="text-base">💝</span>
                  <div>
                    <h4 className="font-bold text-slate-800 text-xs uppercase tracking-wide">2. Optional Additional Support</h4>
                    <p className="text-xs text-slate-500 mt-0.5">Already unlocked the puzzles? You can choose to contribute optional additional support via our Tip Jar to help maintain servers and upkeep.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col w-full gap-3">
              <button
                onClick={() => {
                  setShowSponsorshipExplanation(false);
                  handleGateProtectedAction(() => {
                    onClose();
                    if (onOpenSupport) onOpenSupport();
                  });
                }}
                className="w-full bg-rose-600 hover:bg-rose-700 text-white font-bold py-3.5 px-6 rounded-2xl text-xs tracking-wide shadow-lg hover:shadow-rose-100/50 transition-all active:scale-95 flex items-center justify-center gap-2 cursor-pointer"
              >
                Unlock or Support
              </button>
              
              <button
                onClick={() => setShowSponsorshipExplanation(false)}
                className="w-full bg-slate-50 hover:bg-slate-100 text-slate-500 font-bold py-3 px-6 rounded-2xl text-xs tracking-wide transition-colors active:scale-95 cursor-pointer"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-md" onClick={onClose}></div>
      <div className="relative bg-white w-full max-w-xl max-h-[90vh] rounded-[3rem] shadow-2xl animate-in zoom-in duration-300 flex flex-col overflow-hidden">
        <header className="p-8 border-b border-gray-100 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-blue-50 p-3 rounded-2xl">
              <Settings className="w-6 h-6 text-blue-600" />
            </div>
            <h2 className="font-cinzel font-bold text-2xl text-gray-900">Settings</h2>
          </div>
          <button onClick={onClose} className="p-2 text-gray-300 hover:text-gray-500 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-8 space-y-10">
          {sections.map((section) => (
            <section key={section.title}>
              <div className="flex items-center gap-2 mb-6">
                {section.icon}
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400">{section.title}</h3>
              </div>
              <div className="space-y-6">
                {section.items.map((item) => (
                  <div key={item.label}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-gray-800">{item.label}</p>
                        <p className="text-xs text-gray-400 font-medium">{item.description}</p>
                      </div>
                      {item.control}
                    </div>
                    {item.extra}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        <footer className="p-6 bg-gray-50 text-center">
          <p className="text-[10px] font-bold text-gray-300 uppercase tracking-widest">
            Bible Puzzle Quiz v{APP_VERSION} &copy; 2026
          </p>
        </footer>
      </div>
    </div>
  );
};

export const IntroVideo: React.FC<{ onEnter: () => void }> = ({ onEnter }) => {
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch((err) => {
        console.log('Video autoplay deferred or unavailable:', err);
        setVideoError(true);
      });
    }
  }, []);

  const handleEnterClick = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    // Always call onEnter so Google Play Families review bots & users can access the app seamlessly
    onEnter();
  };

  return (
    <div 
      className="fixed inset-0 flex flex-col items-center justify-center cursor-pointer overflow-hidden z-[100] p-6 pt-safe pb-safe px-safe" 
      style={{ backgroundColor: COBALT_BLUE }} 
      onClick={handleEnterClick}
    >
      <div className="relative w-full max-w-[320px] aspect-square overflow-hidden shadow-2xl rounded-[3rem] border-8 border-white/10 bg-blue-900/20 flex items-center justify-center">
        {!videoError ? (
          <video 
            ref={videoRef}
            autoPlay 
            muted 
            playsInline 
            loop 
            preload="auto"
            onError={() => setVideoError(true)}
            className="w-full h-full object-cover animate-in fade-in duration-500"
          >
            <source src="/assets/videos/bpq-intro-ready.mp4" type="video/mp4" />
          </video>
        ) : (
          <div className="flex flex-col items-center justify-center text-white/80 gap-3 p-6 text-center">
            <BookOpen className="w-16 h-16 text-white animate-bounce mb-2" />
            <span className="text-sm font-bold font-cinzel text-white uppercase tracking-wider">Bible Puzzle Quiz</span>
          </div>
        )}
      </div>
      <div className="mt-12 text-center">
        <h1 className="text-white font-cinzel text-3xl font-bold mb-2">Bible Puzzle Quiz</h1>
        <p className="text-white/60 text-xs uppercase tracking-[0.4em] font-medium">Depict the Word through Play</p>
        <button 
          onClick={handleEnterClick}
          className="mt-10 bg-white text-blue-900 px-10 py-4 rounded-2xl font-bold uppercase tracking-widest text-xs shadow-2xl hover:bg-blue-50 transition-all active:scale-95 cursor-pointer"
        >
          Enter Game
        </button>
      </div>
    </div>
  );
};

export const Login: React.FC<{ onLogin: (u: User) => void }> = ({ onLogin }) => {
  const [method, setMethod] = useState<'profile' | 'guest'>('profile');
  const [username, setUsername] = useState('');
  const [showPrivacy, setShowPrivacy] = useState(false);

  return (
    <div className="flex-1 flex items-center justify-center p-6 min-h-screen pt-safe pb-safe px-safe" style={{ background: `linear-gradient(135deg, ${COBALT_BLUE} 0%, #002d69 100%)` }}>
      <div className="w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl p-8 flex flex-col items-center animate-in fade-in zoom-in duration-500">
        <div className="mb-4 p-4 bg-blue-50 rounded-3xl shadow-inner">
          <BookOpen className="w-10 h-10" style={{ color: COBALT_BLUE }} />
        </div>
        <h1 className="text-2xl font-cinzel font-bold text-gray-800 mb-1 text-center">Bible Puzzle Quiz</h1>
        <p className="text-gray-400 text-xs mb-8 text-center font-medium">Depict the Word through Play</p>

        {/* Tab Switcher */}
        <div className="flex bg-gray-50 p-1 rounded-2xl w-full mb-8 border border-gray-100">
          <button 
            onClick={() => setMethod('profile')}
            className={`flex-1 py-3 text-[10px] font-bold uppercase tracking-widest rounded-xl transition-all cursor-pointer ${method === 'profile' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400'}`}
          >
            Create Profile
          </button>
          <button 
            onClick={() => setMethod('guest')}
            className={`flex-1 py-3 text-[10px] font-bold uppercase tracking-widest rounded-xl transition-all cursor-pointer ${method === 'guest' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400'}`}
          >
            Play as Guest
          </button>
        </div>

        {method === 'profile' && (
          <form 
            className="w-full space-y-5 animate-in fade-in slide-in-from-bottom-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (username.trim()) {
                onLogin({
                  username: username.trim(),
                  isGuest: false,
                  loginMethod: 'profile',
                  avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(username.trim())}`
                });
              } else {
                alert('Please enter a username.');
              }
            }}
          >
            <div className="relative">
              <UserIcon className="absolute left-4 top-4 w-5 h-5 text-gray-300" />
              <input 
                type="text" 
                placeholder="Choose a Username" 
                maxLength={20}
                className="w-full pl-12 pr-4 py-4 border border-gray-100 bg-gray-50 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 transition-all font-semibold text-sm" 
                value={username} 
                onChange={(e) => setUsername(e.target.value)} 
                required
                autoFocus
              />
            </div>
            <button type="submit" className="w-full text-white font-bold py-4 rounded-2xl shadow-xl active:scale-95 transition-transform cursor-pointer" style={{ backgroundColor: COBALT_BLUE }}>
              Create Profile & Play
            </button>
            <div className="p-4 bg-blue-50/50 rounded-2xl border border-blue-100/50 flex gap-3 items-start text-left">
              <Sparkles className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
              <p className="text-[10px] text-blue-700 font-medium leading-relaxed">
                Profile players get access to <strong>2 puzzles in each category</strong>. Become a Supporter to unlock the full library of 100+ puzzles and all Faith Duels.
              </p>
            </div>
          </form>
        )}

        {method === 'guest' && (
          <div className="w-full space-y-5 animate-in fade-in slide-in-from-bottom-2 text-center">
            <button 
              onClick={() => onLogin({ username: 'Guest Player', isGuest: true, loginMethod: 'guest' })}
              className="w-full py-4 rounded-2xl font-bold border-2 border-slate-100 text-slate-700 bg-slate-50 hover:bg-slate-100/50 transition active:scale-95 cursor-pointer"
            >
              Play as Guest
            </button>
            <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100 flex gap-3 items-start text-left">
              <Globe className="w-4 h-4 text-yellow-600 shrink-0 mt-0.5" />
              <p className="text-[10px] text-amber-800 font-medium leading-relaxed">
                Guest players can solve only <strong>1 puzzle in every category</strong> (10 total) and cannot participate in "Faith Duels".
              </p>
            </div>
          </div>
        )}

        <button 
          onClick={() => setShowPrivacy(true)}
          className="mt-8 text-[10px] text-gray-300 hover:text-gray-400 font-bold uppercase tracking-widest transition-colors cursor-pointer"
        >
          Privacy Policy
        </button>
      </div>

      <PrivacyModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} />
    </div>
  );
};

export const PrivacyModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 pt-safe pb-safe px-safe">
      <div className="absolute inset-0 bg-blue-950/60 backdrop-blur-md" onClick={onClose}></div>
      <div className="relative bg-white w-full max-w-2xl max-h-[80vh] rounded-[2.5rem] p-8 md:p-12 shadow-2xl animate-in zoom-in duration-300 overflow-y-auto">
        <button onClick={onClose} className="absolute top-6 right-6 text-gray-300 hover:text-gray-500 p-2"><X /></button>
        <div className="flex items-center gap-3 mb-8">
          <div className="bg-blue-50 p-3 rounded-2xl">
            <ShieldCheck className="w-6 h-6 text-blue-600" />
          </div>
          <h2 className="font-cinzel font-bold text-2xl text-gray-900">Privacy Policy</h2>
        </div>
        
        <div className="prose prose-sm max-w-none text-gray-600 space-y-6">
          <section>
            <h3 className="font-bold text-gray-900 mb-2">1. Information We Collect</h3>
            <p>We collect information to provide a better experience. This includes display names for guests, and email addresses for registered users. If you use Google Login, we receive your name and email from Google.</p>
          </section>

          <section>
            <h3 className="font-bold text-gray-900 mb-2">2. How We Use Data</h3>
            <p>Your data is used to save puzzle progress, facilitate "Faith Duels" (challenges), and manage sponsorship status. We do not sell your personal information.</p>
          </section>

          <section>
            <h3 className="font-bold text-gray-900 mb-2">3. Storage</h3>
            <p>We use local storage on your device to save game progress. Registered account data is stored securely on our servers.</p>
          </section>

          <section>
            <h3 className="font-bold text-gray-900 mb-2">4. Third Parties</h3>
            <p>We use Google for authentication and Google Play Billing for processing sponsorships. These services have their own privacy policies.</p>
          </section>

          <section>
            <h3 className="font-bold text-gray-900 mb-2">6. Data Deletion</h3>
            <p>You can delete your account and all data via the Settings menu. You may also reset your game progress without deleting your account. For manual deletion requests, contact us at theswgames@gmail.com.</p>
          </section>
        </div>

        <button 
          onClick={onClose}
          className="w-full mt-10 bg-gray-900 text-white py-4 rounded-2xl font-bold hover:bg-gray-800 transition active:scale-95"
        >
          I Understand
        </button>
      </div>
    </div>
  );
};

const CURRENCIES = [
  { code: 'USD', symbol: '$', rate: 1.0, label: 'United States Dollar (USD)' },
  { code: 'EUR', symbol: '€', rate: 0.92, label: 'Euro (EUR)' },
  { code: 'GBP', symbol: '£', rate: 0.79, label: 'British Pound (GBP)' },
  { code: 'NGN', symbol: '₦', rate: 1450.0, label: 'Nigerian Naira (NGN)' },
  { code: 'INR', symbol: '₹', rate: 83.3, label: 'Indian Rupee (INR)' },
  { code: 'CAD', symbol: 'C$', rate: 1.37, label: 'Canadian Dollar (CAD)' },
  { code: 'AUD', symbol: 'A$', rate: 1.50, label: 'Australian Dollar (AUD)' },
  { code: 'ZAR', symbol: 'R', rate: 18.5, label: 'South African Rand (ZAR)' },
  { code: 'KES', symbol: 'KSh', rate: 130.0, label: 'Kenyan Shilling (KES)' },
  { code: 'GHS', symbol: 'GH₵', rate: 14.5, label: 'Ghanaian Cedi (GHS)' },
  { code: 'BRL', symbol: 'R$', rate: 5.15, label: 'Brazilian Real (BRL)' },
  { code: 'JPY', symbol: '¥', rate: 155.0, label: 'Japanese Yen (JPY)' }
];

const detectLocalCurrency = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    const locale = navigator.language || "";
    
    if (tz.includes("Lagos") || locale.includes("NG")) return CURRENCIES.find(c => c.code === 'NGN') || CURRENCIES[0];
    if (tz.includes("London") || locale.includes("GB") || locale.includes("en-GB")) return CURRENCIES.find(c => c.code === 'GBP') || CURRENCIES[0];
    if (tz.includes("Kolkata") || tz.includes("Calcutta") || locale.includes("IN")) return CURRENCIES.find(c => c.code === 'INR') || CURRENCIES[0];
    if (tz.includes("Europe") || locale.includes("FR") || locale.includes("DE") || locale.includes("IT") || locale.includes("ES") || locale.includes("NL") || locale.includes("BE")) return CURRENCIES.find(c => c.code === 'EUR') || CURRENCIES[0];
    if (tz.includes("Toronto") || tz.includes("Vancouver") || locale.includes("CA")) return CURRENCIES.find(c => c.code === 'CAD') || CURRENCIES[0];
    if (tz.includes("Sydney") || tz.includes("Melbourne") || tz.includes("Australia") || locale.includes("AU")) return CURRENCIES.find(c => c.code === 'AUD') || CURRENCIES[0];
    if (tz.includes("Johannesburg") || locale.includes("ZA")) return CURRENCIES.find(c => c.code === 'ZAR') || CURRENCIES[0];
    if (tz.includes("Nairobi") || locale.includes("KE")) return CURRENCIES.find(c => c.code === 'KES') || CURRENCIES[0];
    if (tz.includes("Accra") || locale.includes("GH")) return CURRENCIES.find(c => c.code === 'GHS') || CURRENCIES[0];
    if (tz.includes("Sao_Paulo") || locale.includes("BR")) return CURRENCIES.find(c => c.code === 'BRL') || CURRENCIES[0];
    if (tz.includes("Tokyo") || locale.includes("JP")) return CURRENCIES.find(c => c.code === 'JPY') || CURRENCIES[0];
  } catch (err) {
    // catch fallback
  }
  return CURRENCIES[0]; // USD
};

export const SupportModal: React.FC<{ isOpen: boolean; onClose: () => void; onUnLockAll: () => void; isAlreadyUnlocked?: boolean }> = ({ isOpen, onClose, onUnLockAll, isAlreadyUnlocked = false }) => {
  const [showGate, setShowGate] = useState(false);
  const [step, setStep] = useState<'info' | 'payment-options' | 'processing' | 'success'>('info');
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [currency, setCurrency] = useState(() => detectLocalCurrency());
  
  // Product type selection: 'puzzles_unlock' (one-time) vs 'optional_tip_jar' (repeatable)
  const [purchaseType, setPurchaseType] = useState<'puzzles_unlock' | 'optional_tip_jar'>(() => 
    isAlreadyUnlocked ? 'optional_tip_jar' : 'puzzles_unlock'
  );
  
  // Local states for restore operation feedback
  const [isRestoring, setIsRestoring] = useState(false);
  const [billingMessage, setBillingMessage] = useState('');
  const [isSimulatedResult, setIsSimulatedResult] = useState(false);

  if (!isOpen) return null;

  const currentUSDAmount = selectedAmount || 0;

  const getEquivalentText = (usdValue: number) => {
    const converted = usdValue * currency.rate;
    return `${currency.symbol}${converted.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${currency.code}`;
  };

  const handleRestorePurchases = async () => {
    setIsRestoring(true);
    try {
      const isNative = isNativePlayBillingSupported();
      const success = await restorePurchases(() => {
        onUnLockAll();
        setStep('success');
      });

      if (success) {
        alert('Success! Your permanent Bible Puzzle Quiz Sponsorship has been successfully restored from Google Play.');
        onClose();
      } else {
        if (!isNative) {
          alert('Note: Google Play Billing service is only available when running inside our Android app. Restoring sandbox state... fully unlocked!');
          onUnLockAll();
          setStep('success');
        } else {
          alert('No active sponsorship entitlements were found on this Google Play Account. If you made a purchase, please make sure you are logged into the correct Google Play profile.');
        }
      }
    } catch (e) {
      console.error(e);
      alert('An error occurred while connecting to the Google Play Store.');
    } finally {
      setIsRestoring(false);
    }
  };

  const handleSupportClick = async () => {
    if (currentUSDAmount <= 0) {
      alert("Please select a valid support amount.");
      return;
    }

    const val = Math.round(currentUSDAmount);
    if (val < 1 || val > 5) {
      alert("Google Play Billing rules require choosing one of the pre-authorized Tier levels ($1, $2, $3, $4, or $5). Please select a direct button amount.");
      return;
    }

    // Determine specific product option details mapping to Google Play Console settings
    // Product 1: puzzles_unlock -> unlock-1 to unlock-5
    // Product 2: optional_tip_jar -> support-1 to support-5
    const optionId = purchaseType === 'puzzles_unlock' ? `unlock-${val}` : `support-${val}`;
    const offerTag = `tier${val}`;

    setStep('processing');
    
    try {
      const result = await makePlayPurchase(purchaseType, optionId, offerTag, val);
      
      if (result.success) {
        setBillingMessage(result.message);
        setIsSimulatedResult(result.isSimulated);
        
        // Unlock features for the session
        onUnLockAll();
        
        // Move to success screen
        setStep('success');
      } else {
        alert(result.message);
        setStep('payment-options');
      }
    } catch (e: any) {
      alert(e?.message || 'Transaction failed. Please try again.');
      setStep('payment-options');
    }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 pt-safe pb-safe px-safe">
      <ParentalGate 
        isOpen={showGate} 
        onClose={() => setShowGate(false)} 
        onSuccess={() => {
          setStep('payment-options');
        }}
        title="Adult Verification"
        message="To continue with this purchase, please have an adult solve this sum:"
      />
      <div className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-white w-full max-w-md rounded-[3rem] p-10 shadow-2xl animate-in zoom-in duration-300 max-h-[90vh] flex flex-col overflow-y-auto">
        <button onClick={onClose} className="absolute top-6 right-6 text-gray-300 hover:text-gray-500 z-10"><X /></button>
        
        {step === 'info' && (
          <div className="flex flex-col items-center">
            <div className="flex justify-center mb-6"><Heart className="w-16 h-16 text-rose-500 fill-rose-500 animate-pulse" /></div>
            <h2 className="text-center font-cinzel font-bold text-2xl mb-4 text-gray-900">
              {isAlreadyUnlocked ? "You're a Supporter!" : "Support the Mission"}
            </h2>
            <p className="text-gray-500 text-center text-sm mb-10 italic leading-relaxed">
              {isAlreadyUnlocked 
                ? "Shalom! Thank you for being a proud sponsor of our mission. Your permanent Google Play Bible Puzzle Quiz Sponsorship is fully active! If you'd like to make an optional contribution to support our servers and upkeep, you can access our optional Tip Jar." 
                : "Guest players are limited to 1 puzzle per category. Profile-created players get 2 puzzles per category. Become a Supporter to unlock the full library of 100+ puzzles and all Faith Duels."
              }
            </p>
            <button 
              onClick={() => setShowGate(true)} 
              className="w-full bg-rose-600 text-white py-5 rounded-2xl font-bold shadow-lg hover:bg-rose-700 transition active:scale-95"
            >
              {isAlreadyUnlocked ? "Open Support Tip Jar" : "Unlock via Google Play"}
            </button>
            
            {!isAlreadyUnlocked && (
              <button 
                onClick={handleRestorePurchases}
                disabled={isRestoring}
                className="w-full mt-3 bg-slate-50 text-slate-600 py-3 rounded-2xl font-bold border border-slate-200 hover:bg-slate-100 transition active:scale-95 text-xs flex items-center justify-center gap-2"
              >
                {isRestoring ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin text-slate-400" />
                    Restoring...
                  </>
                ) : (
                  <>
                    <ShieldCheck className="w-4 h-4 text-emerald-600" />
                    Restore Active Google Play Sponsorship
                  </>
                )}
              </button>
            )}

            <button onClick={onClose} className="w-full mt-6 text-gray-400 text-[10px] font-bold uppercase tracking-[0.2em]">Maybe Later</button>
          </div>
        )}

        {step === 'payment-options' && (
          <div className="flex flex-col">
            <div className="flex items-center gap-3 mb-4 shrink-0">
              <div className="bg-rose-50 p-2.5 rounded-2xl">
                <Heart className="w-6 h-6 text-rose-500 fill-rose-500" />
              </div>
              <h3 className="font-cinzel font-bold text-xl text-gray-900">Become a Supporter</h3>
            </div>
            
            <p className="text-gray-600 text-xs mb-4 leading-relaxed">
              Thanks for using Bible Puzzle Quiz. Unlock all current and future 100+ puzzles, unlimited Faith Duels, and support our mission.
            </p>

            {/* Product Type Toggle Selector */}
            <div className="grid grid-cols-2 gap-2 mb-4 bg-slate-100 p-1.5 rounded-2xl shrink-0">
              <button
                type="button"
                disabled={isAlreadyUnlocked}
                onClick={() => {
                  setPurchaseType('puzzles_unlock');
                }}
                className={`py-2 px-3 text-[11px] font-bold rounded-xl transition ${
                  isAlreadyUnlocked
                    ? 'text-slate-400 opacity-60 cursor-not-allowed bg-slate-50/50'
                    : purchaseType === 'puzzles_unlock' 
                      ? 'bg-white text-rose-600 shadow-md' 
                      : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {isAlreadyUnlocked ? '🔒 Already Unlocked' : '🔑 Permanent Unlock'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setPurchaseType('optional_tip_jar');
                }}
                className={`py-2 px-3 text-[11px] font-bold rounded-xl transition ${
                  purchaseType === 'optional_tip_jar' 
                    ? 'bg-white text-rose-600 shadow-md' 
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                💝 Constant Tip Jar
              </button>
            </div>

            {/* Currency Detector Selector */}
            <div className="mb-4 bg-slate-50 p-3 rounded-2xl border border-slate-100 flex items-center justify-between gap-2.5 shrink-0">
              <div className="flex items-center gap-2">
                <Globe className="w-3.5 h-3.5 text-slate-400 font-bold" />
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Currency:</span>
              </div>
              <select 
                value={currency.code}
                onChange={(e) => {
                  const found = CURRENCIES.find(c => c.code === e.target.value);
                  if (found) setCurrency(found);
                }}
                className="bg-transparent text-[11px] font-bold text-gray-800 outline-none cursor-pointer text-right max-w-[200px]"
              >
                {CURRENCIES.map(curr => (
                  <option key={curr.code} value={curr.code}>
                    {curr.code} ({curr.symbol})
                  </option>
                ))}
              </select>
            </div>

            {/* Options grid */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              {[1, 2, 3, 4, 5].map((amount) => {
                const isSelected = selectedAmount === amount;
                return (
                  <button
                    key={amount}
                    type="button"
                    onClick={() => {
                      setSelectedAmount(amount);
                    }}
                    className={`flex flex-col items-center justify-center p-3 rounded-2xl border transition-all ${
                      amount === 5 ? 'col-span-2' : ''
                    } ${
                      isSelected 
                        ? 'border-rose-600 bg-rose-50/50 ring-2 ring-rose-100' 
                        : 'border-slate-100 bg-slate-50 hover:bg-slate-100/50'
                    }`}
                  >
                    <span className="text-base font-extrabold text-gray-900">${amount}</span>
                    <span className="text-[9px] font-mono font-medium text-slate-500 mt-0.5">
                      ≈ {getEquivalentText(amount)}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Total display & secure billing notification */}
            <div className="bg-slate-50/60 rounded-2xl p-3 border border-slate-100/35 mb-4 text-center space-y-0.5 shrink-0">
              <span className="text-[8px] font-bold text-slate-400 uppercase tracking-wider block">Estimated Billing Total:</span>
              <p className="text-sm font-extrabold text-gray-800">
                {currentUSDAmount > 0 ? getEquivalentText(Math.round(currentUSDAmount)) : "Select an option"}
                {currentUSDAmount > 0 && <span className="text-[11px] font-medium text-slate-400 ml-1">(~${Math.round(currentUSDAmount)} USD)</span>}
              </p>
              <span className="text-[8px] text-slate-400 block font-medium">
                {isNativePlayBillingSupported() 
                  ? "🛡️ Google Play Billing API connection active" 
                  : "🛡️ Standard Web Play Sandbox connection active"}
              </span>
            </div>

            {/* Main Action Buttons */}
            <div className="space-y-3 shrink-0">
              <button 
                onClick={handleSupportClick}
                disabled={currentUSDAmount <= 0}
                className={`w-full py-4 rounded-2xl font-bold font-sans shadow-lg text-xs tracking-wide transition active:scale-95 flex items-center justify-center gap-2 ${
                  currentUSDAmount > 0 
                  ? 'bg-rose-600 text-white hover:bg-rose-700' 
                  : 'bg-slate-100 text-slate-400 cursor-not-allowed shadow-none'
                }`}
              >
                <Heart className="w-4 h-4 text-white fill-white animate-pulse shrink-0" />
                {currentUSDAmount > 0 
                  ? `Support ${getEquivalentText(Math.round(currentUSDAmount))} & Unlock` 
                  : 'Select Support Tier'
                }
              </button>
              
              <button 
                onClick={handleRestorePurchases}
                className="w-full py-2.5 text-[10px] text-blue-600 font-bold uppercase tracking-wider hover:text-blue-800 transition-colors flex items-center justify-center gap-1.5"
              >
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                Restore Purchase
              </button>

              <button 
                onClick={() => setStep('info')} 
                className="w-full py-1.5 text-[9px] text-slate-400 font-bold uppercase tracking-widest hover:text-slate-600 transition-colors"
              >
                Go Back
              </button>
            </div>
          </div>
        )}

        {step === 'processing' && (
          <div className="flex flex-col items-center justify-center py-12 text-center space-y-6">
            <RefreshCw className="w-12 h-12 text-rose-500 animate-spin" />
            <div className="space-y-2">
              <h3 className="font-cinzel font-bold text-lg text-gray-900">Secure Play Billing Processing</h3>
              <p className="text-xs text-gray-500 leading-relaxed max-w-xs">
                Connecting with Google Play payment portal to securely authorize {getEquivalentText(Math.round(currentUSDAmount))} support tier...
              </p>
            </div>
          </div>
        )}

        {step === 'success' && (
          <div className="flex flex-col items-center text-center">
            <div className="bg-emerald-50 p-6 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center relative animate-bounce">
              <Check className="w-10 h-10 text-emerald-600" />
              <Sparkles className="w-5 h-5 text-amber-400 absolute -top-1 -right-1 animate-pulse" />
            </div>
            
            <h3 className="font-cinzel font-bold text-2xl text-gray-900 mb-3">Support Activated!</h3>
            
            <p className="text-gray-500 text-sm mb-6 max-w-sm leading-relaxed font-sans">
              {billingMessage || `Thank you so much for supporting us with ${getEquivalentText(Math.round(currentUSDAmount))} (~$${Math.round(currentUSDAmount)} USD).`}
            </p>
            
            <div className="bg-emerald-50/50 p-5 rounded-[2rem] border border-emerald-100 w-full mb-8 text-left space-y-3">
              <p className="text-[10px] font-bold text-emerald-800 uppercase tracking-wider flex items-center gap-1.5 mb-1">
                <ShieldCheck className="w-4 h-4 shrink-0" />
                Sponsorship Credentials Provisioned
              </p>
              <ul className="text-xs text-gray-600 leading-relaxed space-y-1.5 list-disc pl-4 font-sans font-medium">
                <li>Unlocked full library of 100+ Bible puzzles!</li>
                <li>Unlimited Faith Duel challenge link generations enabled.</li>
                <li>Access synchronized to your registered display player ID.</li>
                {isSimulatedResult && (
                  <li className="text-[10px] text-amber-600 font-bold">Tested in Web Sandbox Simulator Mode successfully.</li>
                )}
              </ul>
            </div>

            <button 
              type="button"
              onClick={() => {
                onClose();
              }}
              className="w-full bg-emerald-600 text-white py-4 px-6 rounded-2xl font-bold shadow-md hover:bg-emerald-700 transition active:scale-95 text-sm"
            >
              Enjoy All Unlocked Features!
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export const AuthRestrictionModal: React.FC<{ isOpen: boolean; onClose: () => void; onGoToLogin: () => void }> = ({ isOpen, onClose, onGoToLogin }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-4 pt-safe pb-safe px-safe">
      <div className="absolute inset-0 bg-gray-950/60 backdrop-blur-md" onClick={onClose}></div>
      <div className="relative bg-white w-full max-sm rounded-[2.5rem] p-10 shadow-2xl animate-in zoom-in duration-300 text-center">
        <div className="bg-blue-50 p-6 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
          <Globe className="w-10 h-10 text-blue-600" />
        </div>
        <h2 className="font-cinzel font-bold text-xl mb-3 text-gray-900">Sponsorship Required</h2>
        <p className="text-gray-500 text-xs mb-8 leading-relaxed font-medium">
          Only <strong>Supporters</strong> can create Faith Duel challenges. Please support the mission to unlock this feature.
        </p>
        <div className="space-y-3">
          <button 
            onClick={onGoToLogin} 
            className="w-full bg-blue-700 text-white py-4 rounded-2xl font-bold shadow-lg flex items-center justify-center gap-2"
          >
            Go to Login
            <ArrowRight className="w-4 h-4" />
          </button>
          <button 
            onClick={onClose} 
            className="w-full py-4 text-[10px] text-gray-400 font-bold uppercase tracking-widest"
          >
            Not Now
          </button>
        </div>
      </div>
    </div>
  );
};

export const BioModal: React.FC<{ 
  user: any; 
  progress?: any;
  isOpen: boolean; 
  onClose: () => void; 
  isCurrentUser?: boolean; 
  onUpdateAvatar?: (avatar: string) => void 
}> = ({ user, progress, isOpen, onClose, isCurrentUser, onUpdateAvatar }) => {
  if (!isOpen) return null;

  const avatars = [
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Felix',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Aneka',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Jasper',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Luna',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Oliver',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Willow'
  ];

  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center p-4 pt-safe pb-safe px-safe">
      <div className="absolute inset-0 bg-gray-950/60 backdrop-blur-md" onClick={onClose}></div>
      <div className="relative bg-white w-full max-w-sm rounded-[2.5rem] p-8 shadow-2xl text-center animate-in zoom-in duration-300">
        <button onClick={onClose} className="absolute top-6 right-6 text-gray-300 hover:text-gray-500"><X /></button>
        
        <div className="relative group mx-auto mb-4 w-24 h-24">
          <div className="bg-blue-50 p-1 rounded-full w-full h-full flex items-center justify-center overflow-hidden border-2 border-blue-100">
            {user?.avatar ? (
              <img src={user.avatar} alt="Avatar" className="w-full h-full object-cover" />
            ) : (
              <UserIcon className="w-12 h-12 text-blue-600" />
            )}
          </div>
          {isCurrentUser && (
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-full flex items-center justify-center cursor-pointer">
              <Settings className="w-6 h-6 text-white" />
            </div>
          )}
        </div>

        <h2 className="font-cinzel font-bold text-2xl mb-1 text-gray-900">{user?.username || user?.name || 'Player'}</h2>
        <p className="text-gray-500 text-xs mb-6 font-medium uppercase tracking-widest">Faith Warrior</p>
        
        {isCurrentUser && (
          <div className="flex justify-center gap-2 mb-6">
            {avatars.map((av) => (
              <button 
                key={av} 
                onClick={() => onUpdateAvatar?.(av)}
                className={`w-8 h-8 rounded-full overflow-hidden border-2 transition-all ${user?.avatar === av ? 'border-blue-600 scale-110' : 'border-transparent opacity-60 hover:opacity-100'}`}
              >
                <img src={av} alt="Avatar option" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 flex flex-col items-center">
            <Scroll className="w-4 h-4 text-blue-600 mb-1" />
            <p className="text-lg font-bold text-gray-900">{progress?.solvedPuzzles?.length || 0}</p>
            <p className="text-[8px] uppercase tracking-wider text-slate-400 font-bold">Scrolls Solved</p>
          </div>
          <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 flex flex-col items-center">
            <Swords className="w-4 h-4 text-rose-600 mb-1" />
            <p className="text-lg font-bold text-gray-900">{progress?.totalDuelCount || 0}</p>
            <p className="text-[8px] uppercase tracking-wider text-slate-400 font-bold">Duels Done</p>
          </div>
        </div>

        {progress?.totalDuelCount > 0 && (
          <div className="grid grid-cols-2 gap-2 mb-6">
            <div className="bg-amber-50/50 p-3 rounded-xl border border-amber-100 flex flex-col items-center">
              <p className="text-sm font-bold text-amber-900">
                {Math.round(progress.totalDuelTime / progress.totalDuelCount)}s
              </p>
              <p className="text-[7px] uppercase tracking-tight text-amber-600 font-bold">Avg Duel Time</p>
            </div>
            <div className="bg-sky-50/50 p-3 rounded-xl border border-sky-100 flex flex-col items-center">
              <p className="text-sm font-bold text-sky-900">
                {Math.round(progress.totalDuelMoves / progress.totalDuelCount)}
              </p>
              <p className="text-[7px] uppercase tracking-tight text-sky-600 font-bold">Avg Duel Moves</p>
            </div>
          </div>
        )}

        <div className="bg-blue-600 p-4 rounded-2xl mb-2 shadow-lg shadow-blue-200">
          <p className="text-[10px] uppercase tracking-[0.2em] text-blue-100 font-bold mb-1">Total Faith Score</p>
          <p className="text-3xl font-cinzel font-bold text-white">
            {Math.round(progress?.totalFaithScore || 0)}
          </p>
        </div>

        {progress?.lifetimeDuelCount > 0 && (
          <div className="bg-blue-50/50 p-2 rounded-xl mb-6 border border-blue-100">
             <p className="text-[10px] uppercase tracking-widest text-blue-600 font-bold">
               Avg Faith Score: {Math.round((progress.totalFaithScore / progress.lifetimeDuelCount) * 10) / 10}
             </p>
          </div>
        )}
      </div>
    </div>
  );
};

export const InstructionsModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center p-4 pt-safe pb-safe px-safe">
      <div className="absolute inset-0 bg-gray-950/60 backdrop-blur-md" onClick={onClose}></div>
      <div className="relative bg-white w-full max-w-lg rounded-[2.5rem] p-8 shadow-2xl animate-in zoom-in duration-300 overflow-y-auto max-h-[90vh]">
        <button onClick={onClose} className="absolute top-6 right-6 text-gray-300 hover:text-gray-500"><X /></button>
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-blue-50 p-3 rounded-2xl">
            <BookOpen className="w-6 h-6 text-blue-600" />
          </div>
          <h2 className="font-cinzel font-bold text-2xl text-gray-900">How to Play</h2>
        </div>
        
        <div className="space-y-6 text-sm text-gray-600 mb-8">
          <section>
            <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-2">
              <UserIcon className="w-4 h-4 text-blue-600" /> 1. Login
            </h3>
            <p>Create a <strong>Profile</strong> by simply choosing a username, or play immediately as a <strong>Guest</strong>. Profile players enjoy extra access limits.</p>
          </section>

          <section>
            <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-2">
              <Play className="w-4 h-4 text-emerald-600" /> 2. Access Levels
            </h3>
            <p><strong>Guests</strong> access 1 puzzle per category. <strong>Profile-created</strong> players access 2 puzzles per category. <strong>Supporters</strong> unlock the full library of 100+ puzzles and all Faith Duels.</p>
          </section>

          <section>
            <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-2">
              <BookOpen className="w-4 h-4 text-amber-600" /> 3. Identify the Source
            </h3>
            <p>Solve the scripture quiz (Book, Chapter, Verse) to unlock the illustration. Use the <strong>Coded Hint</strong> if you need help!</p>
          </section>

          <section>
            <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-2">
              <Swords className="w-4 h-4 text-rose-600" /> 4. Faith Duels
            </h3>
            <p>Challenge friends to solve puzzles. <strong>Supporters</strong> can use any puzzle for duels. Guests and standard profile players cannot initiate duels.</p>
          </section>

          <section className="bg-blue-50 p-4 rounded-2xl border border-blue-100">
            <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-2">
              <Heart className="w-4 h-4 text-rose-500" /> 5. Sponsorship & Progress
            </h3>
            <p className="text-[11px]">Progress is saved automatically. Become an <strong>Activated Member</strong> to support the mission and unlock all content.</p>
          </section>
        </div>

        <button onClick={onClose} className="w-full bg-blue-600 text-white py-4 rounded-2xl font-bold shadow-lg hover:bg-blue-700 transition active:scale-95">
          Understood
        </button>
      </div>
    </div>
  );
};

export const ExitConfirmModal: React.FC<{ isOpen: boolean; onClose: () => void; onConfirm: () => void; title?: string; message?: string }> = ({ isOpen, onClose, onConfirm, title, message }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 pt-safe pb-safe px-safe">
      <div className="absolute inset-0 bg-gray-950/60 backdrop-blur-md" onClick={onClose}></div>
      <div className="relative bg-white w-full max-w-sm rounded-[2.5rem] p-8 shadow-2xl text-center animate-in zoom-in duration-300">
        <div className="bg-amber-50 p-6 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
          <ShieldAlert className="w-10 h-10 text-amber-500" />
        </div>
        <h2 className="font-cinzel font-bold text-xl mb-3 text-gray-900">{title || 'Abandon Quiz?'}</h2>
        <p className="text-gray-500 text-xs mb-8 leading-relaxed font-medium">
          {message || 'Your current progress in this puzzle will be lost. Are you sure you want to return to the catalogue?'}
        </p>
        <div className="flex flex-col gap-3">
          <button 
            onClick={onConfirm} 
            className="w-full bg-rose-600 text-white py-4 rounded-2xl font-bold shadow-lg hover:bg-rose-700 transition active:scale-95"
          >
            Yes, Exit
          </button>
          <button 
            onClick={onClose} 
            className="w-full py-4 text-[10px] text-gray-400 font-bold uppercase tracking-widest"
          >
            Continue Playing
          </button>
        </div>
      </div>
    </div>
  );
};
